import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  RadioButtonUnchecked as InactiveIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import * as portfolioApi from '../lib/api/portfolios';
import * as sectorConfigApi from '../lib/api/sector-config';

// Use Portfolio type from API service
type Portfolio = portfolioApi.Portfolio;

interface CreatePortfolioRequest {
  name: string;
  description?: string;
  symbols: string[];
  type: 'STATIC' | 'DYNAMIC';
}

interface SectorConfig {
  symbol: string;
  name: string;
  enabled: boolean;
}

const AVAILABLE_SECTORS: SectorConfig[] = [
  { symbol: 'XLK', name: 'Technology', enabled: true },
  { symbol: 'XLF', name: 'Financial', enabled: false },
  { symbol: 'XLV', name: 'Healthcare', enabled: false },
  { symbol: 'XLE', name: 'Energy', enabled: false },
  { symbol: 'XLY', name: 'Consumer Discretionary', enabled: false },
  { symbol: 'XLP', name: 'Consumer Staples', enabled: false },
  { symbol: 'XLI', name: 'Industrial', enabled: false },
  { symbol: 'XLB', name: 'Materials', enabled: false },
  { symbol: 'XLRE', name: 'Real Estate', enabled: false },
  { symbol: 'XLU', name: 'Utilities', enabled: false },
  { symbol: 'XLC', name: 'Communication Services', enabled: false },
];

export default function SettingsPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Portfolio state
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [portfolioError, setPortfolioError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [activePortfolioId, setActivePortfolioId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreatePortfolioRequest>({
    name: '',
    description: '',
    symbols: [],
    type: 'STATIC',
  });
  const [symbolInput, setSymbolInput] = useState('');

  // Sector state
  const [sectors, setSectors] = useState<SectorConfig[]>(AVAILABLE_SECTORS);
  const [sectorSaveStatus, setSectorSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [sectorError, setSectorError] = useState<string | null>(null);

  // Display settings state
  const [timezone, setTimezone] = useState<string>('Asia/Taipei');
  const [displaySaveStatus, setDisplaySaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    loadPortfolios();
    loadSectorPreferences();
    loadDisplayPreferences();
  }, []);

  // Normalize symbol inputs/arrays into a clean string[]
  const normalizeSymbols = (input: unknown): string[] => {
    const toParts = (val: unknown): string[] => {
      if (Array.isArray(val)) {
        return val.flatMap((item) => toParts(item as unknown));
      }
      if (typeof val === 'string') {
        return val
          .split(/[\s,;\n\r]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
      }
      return [];
    };

    const parts = toParts(input).map((s) => s.toUpperCase());
    return Array.from(new Set(parts));
  };

  // Portfolio functions - Using centralized API service
  const loadPortfolios = async () => {
    try {
      setLoadingPortfolios(true);
      const data = await portfolioApi.getPortfolios();
      setPortfolios(data);
      const active = data.find((p: Portfolio) => p.isActive);
      if (active) setActivePortfolioId(active.id);
    } catch (err) {
      setPortfolioError(err instanceof Error ? err.message : 'Failed to load portfolios');
    } finally {
      setLoadingPortfolios(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        symbols: normalizeSymbols(symbolInput),
      };
      await portfolioApi.createPortfolio(payload);
      await loadPortfolios();
      handleCloseDialog();
    } catch (err) {
      setPortfolioError(err instanceof Error ? err.message : 'Failed to create portfolio');
    }
  };

  const handleUpdate = async () => {
    if (!selectedPortfolio) return;

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        symbols: normalizeSymbols(symbolInput),
      };
      await portfolioApi.updatePortfolio(selectedPortfolio.id, payload);
      await loadPortfolios();
      handleCloseDialog();
    } catch (err) {
      setPortfolioError(err instanceof Error ? err.message : 'Failed to update portfolio');
    }
  };

  const handleDelete = async () => {
    if (!selectedPortfolio) return;

    try {
      await portfolioApi.deletePortfolio(selectedPortfolio.id);
      await loadPortfolios();
      setDeleteDialogOpen(false);
      setSelectedPortfolio(null);
    } catch (err) {
      setPortfolioError(err instanceof Error ? err.message : 'Failed to delete portfolio');
    }
  };

  const handleActivate = async (portfolioId: string) => {
    try {
      await portfolioApi.activatePortfolio(portfolioId);
      setActivePortfolioId(portfolioId);
      await loadPortfolios();
    } catch (err) {
      setPortfolioError(err instanceof Error ? err.message : 'Failed to activate portfolio');
    }
  };

  const handleOpenDialog = (portfolio?: Portfolio) => {
    if (portfolio) {
      setSelectedPortfolio(portfolio);
      setFormData({
        name: portfolio.name,
        description: portfolio.description,
        symbols: portfolio.symbols,
        type: portfolio.type,
      });
      setSymbolInput(portfolio.symbols.join(', '));
    } else {
      setSelectedPortfolio(null);
      setFormData({
        name: '',
        description: '',
        symbols: [],
        type: 'STATIC',
      });
      setSymbolInput('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPortfolio(null);
    setPortfolioError(null);
  };

  const handleOpenDeleteDialog = (portfolio: Portfolio) => {
    setSelectedPortfolio(portfolio);
    setDeleteDialogOpen(true);
  };

  // Sector functions (v3.5.0 - API-based)
  const loadSectorPreferences = async () => {
    try {
      setLoadingSectors(true);
      setSectorError(null);

      // Fetch available sectors and current configuration from API
      const [availableSectors, config] = await Promise.all([
        sectorConfigApi.getAvailableSectors(),
        sectorConfigApi.getSectorConfig(),
      ]);

      // Map available sectors to enabled/disabled state based on config
      setSectors(availableSectors.map(sector => ({
        symbol: sector.symbol,
        name: sector.name.replace(' Select Sector SPDR Fund', ''),  // Shorten name
        enabled: config.enabledSectors.includes(sector.symbol),
      })));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load sector preferences';
      setSectorError(errorMsg);
      console.error('Failed to load sector preferences', err);
    } finally {
      setLoadingSectors(false);
    }
  };

  const handleSectorToggle = (symbol: string) => {
    setSectors(prev => prev.map(sector =>
      sector.symbol === symbol ? { ...sector, enabled: !sector.enabled } : sector
    ));
  };

  const handleSaveSectorPreferences = async () => {
    try {
      setSectorSaveStatus('saving');
      setSectorError(null);

      // Save to API
      const enabledSymbols = sectors.filter(s => s.enabled).map(s => s.symbol);
      await sectorConfigApi.updateSectorConfig(enabledSymbols);

      // Dispatch custom event for widgets to detect changes
      window.dispatchEvent(new Event('sectorConfigChanged'));

      setSectorSaveStatus('saved');
      setTimeout(() => setSectorSaveStatus('idle'), 2000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save sector preferences';
      setSectorError(errorMsg);
      setSectorSaveStatus('idle');
      console.error('Failed to save sector preferences', err);
    }
  };

  const enabledSectorCount = sectors.filter(s => s.enabled).length;

  // Display preferences functions
  const DISPLAY_STORAGE_KEY = 'dac_display_preferences';

  const loadDisplayPreferences = () => {
    const saved = localStorage.getItem(DISPLAY_STORAGE_KEY);
    if (saved) {
      try {
        const preferences = JSON.parse(saved);
        setTimezone(preferences.timezone || 'Asia/Taipei');
      } catch (err) {
        console.error('Failed to load display preferences', err);
      }
    }
  };

  const handleSaveDisplayPreferences = () => {
    setDisplaySaveStatus('saving');

    const preferences = {
      timezone,
    };
    localStorage.setItem(DISPLAY_STORAGE_KEY, JSON.stringify(preferences));

    // Dispatch custom event for widgets to detect changes
    window.dispatchEvent(new Event('displayConfigChanged'));

    setTimeout(() => {
      setDisplaySaveStatus('saved');
      setTimeout(() => setDisplaySaveStatus('idle'), 2000);
    }, 500);
  };

  // Common timezones list
  const TIMEZONES = [
    { value: 'Asia/Taipei', label: 'Taipei (GMT+8)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (GMT+8)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (GMT+8)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
    { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
    { value: 'America/New_York', label: 'New York (EST)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
    { value: 'America/Chicago', label: 'Chicago (CST)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  return (
    <>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          System Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure portfolios and sector analysis preferences
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Portfolio Configuration" />
          <Tab label="Sector Selection" />
          <Tab label="Display Settings" />
        </Tabs>
      </Box>

      {/* Portfolio Configuration Tab */}
      {activeTab === 0 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Manage Your Portfolios
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              data-testid="new-portfolio-button"
            >
              New Portfolio
            </Button>
          </Box>

          {portfolioError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPortfolioError(null)} data-testid="portfolio-error-alert">
              {portfolioError}
            </Alert>
          )}

          {loadingPortfolios ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer>
                    <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Stocks</TableCell>
                        <TableCell>Symbols</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portfolios.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No portfolios yet. Create your first portfolio to get started.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        portfolios.map((portfolio) => (
                          <TableRow key={portfolio.id}>
                            <TableCell>
                              <Tooltip title={portfolio.id === activePortfolioId ? 'Active Portfolio' : 'Click to activate'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleActivate(portfolio.id)}
                                  color={portfolio.id === activePortfolioId ? 'success' : 'default'}
                                >
                                  {portfolio.id === activePortfolioId ? <ActiveIcon /> : <InactiveIcon />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {portfolio.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {portfolio.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={portfolio.type}
                                size="small"
                                color={portfolio.type === 'STATIC' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {portfolio.symbols.length} stocks
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {portfolio.symbols.slice(0, 3).join(', ')}
                                {portfolio.symbols.length > 3 && ` +${portfolio.symbols.length - 3}`}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(portfolio)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(portfolio)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Sector Selection Tab */}
      {activeTab === 1 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Sector Analysis Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select which sectors to include in sentiment analysis ({enabledSectorCount} of {sectors.length} enabled)
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSectorPreferences}
              disabled={sectorSaveStatus === 'saving' || loadingSectors}
              data-testid="sector-save-button"
            >
              {sectorSaveStatus === 'saving' ? 'Saving...' : sectorSaveStatus === 'saved' ? 'Saved!' : 'Save Changes'}
            </Button>
          </Box>

          {sectorError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSectorError(null)} data-testid="sector-error-alert">
              {sectorError}
            </Alert>
          )}

          {sectorSaveStatus === 'saved' && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Sector preferences saved successfully
            </Alert>
          )}

          {loadingSectors ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (

          <Card>
            <CardContent>
              <Grid container spacing={2}>
                {sectors.map((sector) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sector.symbol}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: sector.enabled ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                        borderLeft: '3px solid',
                        borderColor: sector.enabled ? 'success.main' : 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: sector.enabled ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 255, 255, 0.06)',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sector.enabled}
                            onChange={() => handleSectorToggle(sector.symbol)}
                            color="success"
                            data-testid={`sector-toggle-${sector.symbol}`}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {sector.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sector.symbol}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Note: Server-Side Storage
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sector preferences are now stored on the server and synced across all your devices.
                  Changes will apply immediately after saving.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
          )}
        </>
      )}

      {/* Display Settings Tab */}
      {activeTab === 2 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Display Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure how data is displayed across the application
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveDisplayPreferences}
              disabled={displaySaveStatus === 'saving'}
            >
              {displaySaveStatus === 'saving' ? 'Saving...' : displaySaveStatus === 'saved' ? 'Saved!' : 'Save Changes'}
            </Button>
          </Box>

          {displaySaveStatus === 'saved' && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Display preferences saved successfully. Refresh the page to apply changes.
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Timezone Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the timezone for displaying cache timestamps and data timestamps
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Display Timezone</InputLabel>
                <Select
                  value={timezone}
                  label="Display Timezone"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Note: Timezone Display
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cache timestamps (L1/L2) and data source timestamps will be displayed in the selected timezone.
                  Changes will take effect after refreshing the page or reloading widgets.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Portfolio Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPortfolio ? 'Edit Portfolio' : 'Create Portfolio'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Portfolio Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'STATIC' | 'DYNAMIC' })}
              >
                <MenuItem value="STATIC">Static (Manual stock selection)</MenuItem>
                <MenuItem value="DYNAMIC">Dynamic (Auto-update from criteria)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Stock Symbols"
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="AAPL, MSFT, GOOGL, NVDA, TSLA"
              helperText="Enter symbols separated by commas, spaces, or newlines"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={selectedPortfolio ? handleUpdate : handleCreate}
            disabled={!formData.name || !symbolInput}
          >
            {selectedPortfolio ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Portfolio</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPortfolio?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
